import type { UserNotification } from '@planuze/admin-contracts';
import {
  Button,
  Checkbox,
  EmptyState,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@planuze/ui';
import { Bell } from 'lucide-react';
import { useNotificationsContent } from '../../content/sections/notifications.js';
import { useNotificationsAdminContent } from '../../content/sections/notifications-admin.js';

const SEVERITY_TONE: Record<
  UserNotification['severity'],
  'info' | 'success' | 'warning' | 'destructive'
> = { info: 'info', success: 'success', warning: 'warning', error: 'destructive' };

interface Props {
  readonly items: readonly UserNotification[];
  /** super_admin/support — libera a coluna de ações e o CTA do empty. */
  readonly canManage: boolean;
  /** Há busca ativa — diferencia "sem resultados" de "sem itens" (ADR-0702). */
  readonly hasFilters: boolean;
  readonly onClearFilters: () => void;
  readonly onCompose: () => void;
  readonly onPublish: (n: UserNotification) => void;
  readonly onDelete: (n: UserNotification) => void;
  readonly publishPending: boolean;
  readonly deletePending: boolean;
  readonly selectedIds: ReadonlySet<string>;
  readonly onToggle: (id: string) => void;
  readonly onToggleAll: (checked: boolean) => void;
}

/* Tabela de notificações sobre o primitive `Table` de @planuze/ui (ADR-0118). Empty diferenciado
   (ADR-0702): sem itens = onboarding com CTA de compor; sem resultados = limpar busca. */
export function NotificationsTable({
  items,
  canManage,
  hasFilters,
  onClearFilters,
  onCompose,
  onPublish,
  onDelete,
  publishPending,
  deletePending,
  selectedIds,
  onToggle,
  onToggleAll,
}: Props) {
  const copy = useNotificationsAdminContent().table;
  const bulkCopy = useNotificationsContent();
  if (items.length === 0) {
    return hasFilters ? (
      <EmptyState
        icon={<Bell className="size-6" />}
        title={copy.emptyFilteredTitle}
        description={copy.emptyFilteredDescription}
        primaryAction={
          <Button variant="outline" onClick={onClearFilters}>
            {copy.clearFilters}
          </Button>
        }
      />
    ) : (
      <EmptyState
        icon={<Bell className="size-6" />}
        title={copy.emptyTitle}
        description={copy.emptyDescription}
        primaryAction={canManage ? <Button onClick={onCompose}>{copy.compose}</Button> : undefined}
      />
    );
  }
  const eligible = items.filter((notification) => notification.status === 'draft');
  const allSelected =
    eligible.length > 0 && eligible.every((notification) => selectedIds.has(notification.id));
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {canManage && (
            <TableHead className="w-10">
              <Checkbox
                checked={allSelected}
                disabled={eligible.length === 0}
                onCheckedChange={(checked) => onToggleAll(checked === true)}
                aria-label={bulkCopy.selectPageAriaLabel}
              />
            </TableHead>
          )}
          <TableHead>{copy.columnNotification}</TableHead>
          <TableHead>{copy.columnSeverity}</TableHead>
          <TableHead>{copy.columnStatus}</TableHead>
          <TableHead>{copy.columnAudience}</TableHead>
          {canManage && <TableHead>{copy.columnActions}</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((n) => (
          <TableRow key={n.id}>
            {canManage && (
              <TableCell>
                <Checkbox
                  checked={selectedIds.has(n.id)}
                  disabled={n.status !== 'draft'}
                  onCheckedChange={() => onToggle(n.id)}
                  aria-label={bulkCopy.selectRowAriaLabel(n.title)}
                />
              </TableCell>
            )}
            <TableCell>
              <div className="max-w-md">
                <p className="truncate font-medium text-foreground">{n.title}</p>
                {n.body !== null && (
                  <p className="truncate text-muted-foreground text-xs">{n.body}</p>
                )}
              </div>
            </TableCell>
            <TableCell>
              <StatusBadge variant={SEVERITY_TONE[n.severity]}>{n.severity}</StatusBadge>
            </TableCell>
            <TableCell>
              <StatusBadge variant={n.status === 'published' ? 'success' : 'neutral'}>
                {n.status === 'published' ? copy.published : copy.draft}
              </StatusBadge>
            </TableCell>
            <TableCell className="text-muted-foreground">
              {n.audience === 'all' ? copy.audienceAll : copy.audienceUsers(n.recipientCount)}
            </TableCell>
            {canManage && (
              <TableCell>
                <div className="flex justify-end gap-2">
                  {n.status === 'draft' && (
                    <Button size="sm" onClick={() => onPublish(n)} disabled={publishPending}>
                      {copy.publish}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onDelete(n)}
                    disabled={deletePending}
                  >
                    {copy.remove}
                  </Button>
                </div>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
